<script lang="ts" setup>
import Button from 'primevue/button'
import Column from 'primevue/column'
import DataTable from 'primevue/datatable'
import TableFilter from '@/components/table/TableFilter.vue'
import TableSearchInput from '@/components/table/TableSearchInput.vue'
import PageHeader from '@/components/global/page-header/PageHeader.vue'
import { onMounted, ref, computed } from 'vue'
import { Icon } from '@iconify/vue'
import { RouterLink, useRouter } from 'vue-router'
import type { Yarn } from '@/types/yarn-types'
import { getYarnsList } from '@/api/yarns'
import type { Supplier } from '@/types/supplier-types'
import { getSupplierList } from '@/api/suppliers'
import {getYarnType} from '@/constants/yarns'
import {getSupplier} from '@/constants/suppliers'

const router = useRouter()
const searchQuery = ref('')
const loading = ref<boolean>(false)
const yarnList = ref<any>([])
const suppliers = ref<Supplier[]>()


const filteredYarnList = computed(() => {
  const query = searchQuery.value.toLowerCase()

  return yarnList.value.filter((yarn: Yarn) =>
    yarn.mergeNumber.toLowerCase().includes(query) ||
    getYarnType(yarn.yarnType)?.toLowerCase().includes(query) ||
    getSupplier(yarn.supplierId, suppliers.value)?.toLowerCase().includes(query));
})


onMounted(async () => {
  yarnList.value = await getYarnsList()
  suppliers.value = await getSupplierList()
})

</script>

<template>
  <PageHeader
    title="Yarn"
    :description="`Total: ${yarnList.length}`"
    buttonText="Add New Yarn"
    buttonIcon="ph:yarn-fill"
    :buttonClick="() => router.push('/yarns/add')"
  />
  <div class="flex justify-end py-2">
    <RouterLink
      to="/yarns/archieved"
      class="flex items-center gap-x-2 group hover:text-primary-default"
    >
      <span>Archived Yarn</span>
      <Icon
        icon="material-symbols:arrow-forward"
        class="text-2xl group-hover:translate-x-2 transition-transform text-primary-500"
      />
    </RouterLink>
  </div>
  <DataTable
    :value="filteredYarnList"
    paginator
    :rows="10"
    tableStyle="min-width: 50rem"
    stripedRows
    paginatorTemplate="PrevPageLink CurrentPageReport NextPageLink"
    :rowsPerPageOptions="[10, 25]"
    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} yarns"
    :loading="loading"
    :pt="{
      table: loading && 'min-h-[30rem]'
    }"
    :pt-options="{
      mergeProps: true
    }"
  >
    <template #empty> <p class="text-center">No yarns found.</p> </template>

    <template #header>
      <div class="flex items-center justify-between font-normal">
        <TableSearchInput v-model:searchQuery="searchQuery" />

<!--        <TableFilter />-->
      </div>
    </template>

    <Column field="mergeNumber" header="Merge" sortable>
      <template #body="slotProps">
        <RouterLink
          :to="`/yarns/${slotProps.data.id}`"
          class="font-medium text-neutral-800 block hover:underline"
        >
          {{ slotProps.data.mergeNumber }}
        </RouterLink>
      </template>
    </Column>
    <Column field="yarnType" header="Yarn Type" sortable>
      <template #body="slotProps">
        {{ getYarnType(slotProps.data.yarnType) }}
      </template>
    </Column>
    <Column field="yarnCount" header="Count" sortable />
    <Column field="yarnColour" header="Colour" sortable/>
    <Column field="kgAvailable" header="Kilograms Available" sortable/>

    <Column field="action">
      <template #body="slotProps">
        <RouterLink
          :to="`/yarns/${slotProps.data.id}`"
          class="flex items-center gap-x-2 group hover:text-primary-default"
        >
          <span> More Details</span>
          <Icon
            icon="material-symbols:more-up"
            class="text-2xl group-hover:translate-x-2 transition-transform text-primary-500"
          />
        </RouterLink>
      </template>
    </Column>

    <!------------------------------------------ pagination---------------------------------------------------->
    <template #paginatorprevpagelinkicon>
      <Button size="small" class="space-x-1 items-center" outlined severity="secondary">
        <Icon icon="material-symbols:chevron-left" class="text-2xl" />
        <span>Previous</span>
      </Button>
    </template>
    <template #paginatornextpagelinkicon>
      <Button size="small" outlined severity="secondary" class="space-x-1 items-center">
        <span>Next</span>
        <Icon icon="material-symbols:chevron-right" class="text-2xl" />
      </Button>
    </template>
  </DataTable>
</template>